import { actions } from "@mixitone/actions";
import { Button } from "@mixitone/components";
import { Account } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { withStateToggle } from "@mixitone/util";
import AppModal from "components/common/AppModal";
import { AppController } from "controllers/AppController";
import MixitoneController from "controllers/MixitoneController";
import { injectModal } from "lib/injectParams";

interface State {
  deleting: boolean;
  sent: boolean;
}

class CancelAccountController extends MixitoneController<State> {
  @withStateToggle("deleting")
  async actionDeleteAccount() {
    await actions.feedback.sendFeedback({
      account_id: Account.current?.id,
      club_name: this.get(AppController).state.currentClub?.name,
      message: "Please delete my account.",
    });
    this.state.sent = true;
  }
}

const CancelAccount: React.FC = () => {
  const appController = AppController.use();
  const online = appController.state.online;
  const controller = CancelAccountController.use();
  const { deleting, sent } = controller.state;

  if (sent) {
    return (
      <AppModal title="Cancel account" className="max-w-64">
        <p>Your account has been queued for deletion.</p>
        <p>
          You will receive an email confirmation once your account has been deleted. Please allow up to 5
          business days for this process.
        </p>
        <div className="mt-4 flex justify-end space-x-2">
          <Button
            onClick={() => {
              controller.actionBack();
            }}
          >
            Close
          </Button>
        </div>
      </AppModal>
    );
  }

  if (!online) {
    return (
      <AppModal title="Cancel account" className="max-w-64">
        <p>You are currently offline. Please reconnect to the internet to cancel your account.</p>
        <div className="mt-4 flex justify-end space-x-2">
          <Button
            onClick={() => {
              controller.actionBack();
            }}
          >
            Close
          </Button>
        </div>
      </AppModal>
    );
  }

  return (
    <AppModal title="Cancel account" className="max-w-64">
      <p>Are you sure you want to cancel your account?</p>
      <p>
        Clicking yes will queue your account for deletion. It may take up to 5 business days to process. Once
        your account is deleted, you will no longer have access to your data.
      </p>
      <p>
        If you would like to download your data please contact{" "}
        <a href="mailto:support@mixitone.com">support@mixitone.com</a>.
      </p>
      <div className="mt-4 flex justify-end space-x-2">
        <Button
          onClick={() => {
            controller.actionBack();
          }}
          disabled={deleting}
        >
          No, keep my account
        </Button>
        <Button
          kind="danger"
          onClick={() => {
            controller.actionDeleteAccount();
          }}
          disabled={deleting}
          spinner={deleting}
        >
          Yes, delete my account
        </Button>
      </div>
    </AppModal>
  );
};

export default injectModal(CancelAccountController.scope(ApplicationView(CancelAccount)));
